import React, { useState, useMemo, useCallback } from 'react';
import {
  Save,
  Download,
  Printer,
  FileText,
  RotateCcw,
  LayoutTemplate,
  CheckCircle2,
  Clock,
  Sparkles,
  Sliders,
  Edit3,
} from 'lucide-react';
import { Button } from '../../shared/components/Button';
import { useToast } from '../../shared/context/ToastContext';
import { COVER_TEMPLATES } from '../../templates/cover';
import { CoverTemplate, CoverTemplateValues } from '../../templates/types';
import { CoverFieldsEditor } from './components/CoverFieldsEditor';
import { TypographyLayoutControls } from './components/TypographyLayoutControls';
import { TemplateSelectorModal } from './components/TemplateSelectorModal';
import { DocumentRenderer } from '../../core/renderer/DocumentRenderer';
import { ManualEditToolbar } from '../../core/manual-edit/ManualEditToolbar';
import { createDefaultPageSettings } from '../../core/ast/factory';
import { PageSettings, DocumentModel, ShapeType } from '../../core/ast/document.types';
import { ExportPreviewModal, ExportSettings } from '../../components/export/ExportPreviewModal';
import * as blockOps from '../../core/operations/blockOps';
import { createShapeBlock } from '../../core/operations/blockOps';
import { saveDocumentToDb } from '../../core/persistence/indexedDbStorage';
import { exportDocumentToPdf, downloadPdfBytes } from '../../core/export/pdfExporter';
import { exportDocumentToDocx, downloadDocxBlob } from '../../core/export/docxExporter';
import { sanitizeFilename } from '../../core/export/documentValidator';

export const CoverStudio: React.FC = () => {
  const { showToast } = useToast();

  // Active Template Selection
  const [activeTemplate, setActiveTemplate] = useState<CoverTemplate>(() => COVER_TEMPLATES[0]);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);

  // User Field Values (Initialized from active template defaults)
  const [values, setValues] = useState<CoverTemplateValues>(() => activeTemplate.defaultValues);

  // Page Settings Overrides
  const [pageSettings, setPageSettings] = useState<PageSettings>(() => createDefaultPageSettings());

  // Manual Edit Mode state
  const [isManualEditMode, setIsManualEditMode] = useState(false);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [customCoverDoc, setCustomCoverDoc] = useState<DocumentModel | null>(null);

  // Left panel active tab
  const [activeTab, setActiveTab] = useState<'fields' | 'styling'>('fields');

  // Autosave & Export Status
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'dirty'>('saved');
  const [isExportPreviewOpen, setIsExportPreviewOpen] = useState(false);
  const [exportPreviewFormat, setExportPreviewFormat] = useState<'pdf' | 'docx'>('pdf');

  // Handle value updates
  const handleValuesChange = useCallback((updates: Partial<CoverTemplateValues>) => {
    setValues((prev) => ({ ...prev, ...updates }));
    setSaveStatus('dirty');
  }, []);

  const handlePageSettingsChange = useCallback((updates: Partial<PageSettings>) => {
    setPageSettings((prev) => ({ ...prev, ...updates }));
    setSaveStatus('dirty');
  }, []);

  // Template Switching (Clones template default values without mutating original definition)
  const handleSelectTemplate = useCallback((template: CoverTemplate) => {
    setActiveTemplate(template);
    setValues((prev) => ({
      ...template.defaultValues,
      // Preserve student and course user inputs across template switches
      studentName: prev.studentName,
      studentId: prev.studentId,
      studentEmail: prev.studentEmail,
      courseCode: prev.courseCode,
      courseTitle: prev.courseTitle,
      facultyName: prev.facultyName,
      universityName: prev.universityName,
      departmentName: prev.departmentName,
      submissionDate: prev.submissionDate,
    }));
    setSaveStatus('dirty');
    showToast(`Switched to "${template.name}" template.`, 'success');
  }, [showToast]);

  // Reset to original template default values
  const handleResetTemplate = () => {
    if (window.confirm('Reset this cover page to original template defaults? Current modifications will be reverted.')) {
      setValues(activeTemplate.defaultValues);
      setPageSettings(createDefaultPageSettings());
      setSaveStatus('dirty');
      showToast('Template reset to original defaults.', 'info');
    }
  };

  // Compile to canonical DocumentModel AST
  const compiledDocument: DocumentModel = useMemo(() => {
    return activeTemplate.compileToDocument(values, pageSettings);
  }, [activeTemplate, values, pageSettings]);

  const activeDoc = customCoverDoc || compiledDocument;

  const handleUpdateBlockData = (sectionId: string, blockId: string, updates: any) => {
    setCustomCoverDoc((prev) => {
      const base = prev || compiledDocument;
      return {
        ...base,
        sections: base.sections.map((sec) => {
          if (sec.id !== sectionId) return sec;
          return {
            ...sec,
            blocks: sec.blocks.map((b) => (b.id === blockId ? { ...b, data: { ...b.data, ...updates } } : b)),
          };
        }),
      };
    });
    setSaveStatus('dirty');
  };

  const handleRemoveBlock = (sectionId: string, blockId: string) => {
    setCustomCoverDoc((prev) => {
      const base = prev || compiledDocument;
      return {
        ...base,
        sections: base.sections.map((sec) => {
          if (sec.id !== sectionId) return sec;
          return {
            ...sec,
            blocks: sec.blocks.filter((b) => b.id !== blockId),
          };
        }),
      };
    });
    setSaveStatus('dirty');
  };

  const handleDuplicateBlock = (sectionId: string, blockId: string) => {
    setCustomCoverDoc((prev) => {
      const base = prev || compiledDocument;
      return {
        ...base,
        sections: base.sections.map((sec) => {
          if (sec.id !== sectionId) return sec;
          const idx = sec.blocks.findIndex((b) => b.id === blockId);
          if (idx === -1) return sec;
          const orig = sec.blocks[idx];
          const dupe = {
            ...orig,
            id: `blk-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`,
            createdAt: new Date().toISOString(),
            data: JSON.parse(JSON.stringify(orig.data)),
          };
          const newBlocks = [...sec.blocks];
          newBlocks.splice(idx + 1, 0, dupe);
          return { ...sec, blocks: newBlocks };
        }),
      };
    });
    setSaveStatus('dirty');
  };

  const handleInsertShape = (sectionId: string, shapeType: ShapeType) => {
    const shapeBlock = createShapeBlock(shapeType);
    setCustomCoverDoc((prev) => {
      const base = prev || compiledDocument;
      return {
        ...base,
        sections: base.sections.map((sec) => {
          if (sec.id !== sectionId) return sec;
          return {
            ...sec,
            blocks: [...sec.blocks, shapeBlock],
          };
        }),
      };
    });
    setSaveStatus('dirty');
  };

  const handleNudgeBlock = (sectionId: string, blockId: string, dx: number, dy: number) => {
    setCustomCoverDoc((prev) => {
      const base = prev || compiledDocument;
      return blockOps.nudgeBlock(base, sectionId, blockId, dx, dy);
    });
    setSaveStatus('dirty');
  };

  // Manual & Persistent Save
  const handleManualSave = async () => {
    setSaveStatus('saving');
    try {
      await saveDocumentToDb(activeDoc);
      setSaveStatus('saved');
      showToast('Cover page saved to local storage.', 'success');
    } catch (e) {
      setSaveStatus('dirty');
      showToast('Failed to save cover page.', 'error');
    }
  };

  const handleExecutePdfExport = async (exportDoc: DocumentModel, settings: ExportSettings) => {
    try {
      const pdfBytes = await exportDocumentToPdf(exportDoc, { isGrayscale: settings.colorMode === 'grayscale' });
      const filename = sanitizeFilename(values.title || 'Cover_Page', 'pdf');
      downloadPdfBytes(pdfBytes, filename);
      showToast(`Exported "${filename}" successfully!`, 'success');
    } catch (err) {
      console.error('Failed to export PDF:', err);
      showToast('Error generating PDF cover page.', 'error');
      throw err;
    }
  };

  const handleExecuteDocxExport = async (exportDoc: DocumentModel, _settings: ExportSettings) => {
    try {
      const docxBlob = await exportDocumentToDocx(exportDoc);
      const filename = sanitizeFilename(values.title || 'Cover_Page', 'docx');
      downloadDocxBlob(docxBlob, filename);
      showToast(`Exported "${filename}" successfully!`, 'success');
    } catch (err) {
      console.error('Failed to export DOCX:', err);
      showToast('Error generating Word cover page.', 'error');
      throw err;
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Studio Header Toolbar */}
      <div
        style={{
          height: '52px',
          borderBottom: '1px solid var(--border-subtle)',
          backgroundColor: 'var(--bg-surface)',
          padding: '0 var(--space-6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          gap: 'var(--space-3)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-sm)', backgroundColor: 'var(--color-info-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-info)' }}>
            <FileText size={16} />
          </div>
          <div>
            <h2 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
              <span>Cover Studio</span>
              <span className="badge badge-primary" style={{ fontSize: '10px' }}>
                <Sparkles size={10} /> {activeTemplate.name}
              </span>
            </h2>
          </div>

          {/* Filename Input Control */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
            <label
              htmlFor="cover-filename-input"
              style={{
                fontSize: '11px',
                fontWeight: 700,
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                whiteSpace: 'nowrap',
              }}
            >
              File name:
            </label>
            <input
              id="cover-filename-input"
              type="text"
              className="form-input"
              value={values.title || ''}
              placeholder="e.g. Lab_Report_Cover_Page"
              onChange={(e) => {
                const sanitized = e.target.value.replace(/[\\/:*?"<>|]/g, '');
                handleValuesChange({ title: sanitized });
              }}
              style={{
                maxWidth: '220px',
                minWidth: '120px',
                fontWeight: 600,
                height: '30px',
                padding: '2px 8px',
                fontSize: '12px',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: 'var(--bg-surface)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-default)',
              }}
              title="Enter document and export file name (extension .pdf/.docx is handled automatically)"
            />
          </div>

          {/* Status Indicator */}
          {saveStatus === 'saved' ? (
            <span className="badge badge-success" style={{ fontSize: '10px' }}>
              <CheckCircle2 size={10} /> Saved
            </span>
          ) : saveStatus === 'saving' ? (
            <span className="badge badge-neutral" style={{ fontSize: '10px' }}>
              <Clock size={10} /> Saving...
            </span>
          ) : (
            <span className="badge badge-neutral" style={{ fontSize: '10px', color: 'var(--color-warning)' }}>
              • Unsaved
            </span>
          )}
        </div>

        {/* Toolbar Action Buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          {/* Manual Edit Mode Toggle Button */}
          <button
            type="button"
            className={`btn ${isManualEditMode ? 'btn-primary' : 'btn-secondary'} btn-sm`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: 600,
              backgroundColor: isManualEditMode ? 'var(--primary-600)' : undefined,
              color: isManualEditMode ? '#ffffff' : undefined,
            }}
            onClick={() => setIsManualEditMode(!isManualEditMode)}
            title="Toggle Word + Canva interactive Manual Edit Mode"
          >
            <Edit3 size={13} />
            <span>Manual Edit</span>
            {isManualEditMode && (
              <span
                style={{
                  fontSize: '9px',
                  backgroundColor: '#ffffff',
                  color: 'var(--primary-700)',
                  padding: '1px 5px',
                  borderRadius: '10px',
                  fontWeight: 700,
                }}
              >
                ON
              </span>
            )}
          </button>

          <span style={{ color: 'var(--border-default)', margin: '0 4px' }}>|</span>

          <Button
            variant="secondary"
            size="sm"
            icon={<LayoutTemplate size={14} />}
            onClick={() => setIsTemplateModalOpen(true)}
          >
            Change Template
          </Button>

          <Button
            variant="ghost"
            size="sm"
            icon={<RotateCcw size={13} />}
            onClick={handleResetTemplate}
            title="Reset to Template Defaults"
          >
            Reset
          </Button>

          <span style={{ color: 'var(--border-default)', margin: '0 4px' }}>|</span>

          <Button
            variant="ghost"
            size="sm"
            icon={<Save size={14} />}
            onClick={handleManualSave}
          >
            Save Draft
          </Button>

          <Button
            variant="ghost"
            size="sm"
            icon={<Printer size={14} />}
            onClick={() => window.print()}
          >
            Print
          </Button>

          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setExportPreviewFormat('docx');
              setIsExportPreviewOpen(true);
            }}
          >
            DOCX
          </Button>

          <Button
            variant="primary"
            size="sm"
            icon={<Download size={14} />}
            onClick={() => {
              setExportPreviewFormat('pdf');
              setIsExportPreviewOpen(true);
            }}
          >
            Export PDF
          </Button>
        </div>
      </div>

      {/* Split-Pane Studio Layout */}
      <div className="split-workspace">
        {/* Left Editor Panel */}
        <div className="split-left-panel" style={{ padding: 'var(--space-4)', gap: 'var(--space-3)' }}>
          {/* Tabs: Fields vs Styling */}
          <div className="tabs-header" style={{ marginBottom: 'var(--space-2)' }}>
            <button
              type="button"
              className={`tab-btn ${activeTab === 'fields' ? 'active' : ''}`}
              onClick={() => setActiveTab('fields')}
            >
              <FileText size={13} style={{ display: 'inline', marginRight: 4 }} />
              Content Fields
            </button>
            <button
              type="button"
              className={`tab-btn ${activeTab === 'styling' ? 'active' : ''}`}
              onClick={() => setActiveTab('styling')}
            >
              <Sliders size={13} style={{ display: 'inline', marginRight: 4 }} />
              Typography & Style
            </button>
          </div>

          {activeTab === 'fields' ? (
            <CoverFieldsEditor
              values={values}
              supportedFields={activeTemplate.supportedFields}
              onChange={handleValuesChange}
            />
          ) : (
            <TypographyLayoutControls
              pageSettings={pageSettings}
              accentColor={values.accentColor}
              borderStyle={values.borderStyle}
              onUpdatePageSettings={handlePageSettingsChange}
              onUpdateAccentColor={(color) => handleValuesChange({ accentColor: color })}
              onUpdateBorderStyle={(bStyle) => handleValuesChange({ borderStyle: bStyle })}
            />
          )}
        </div>

        {/* Right Live A4 Document Preview Panel */}
        <div className="split-right-panel" style={{ display: 'flex', flexDirection: 'column', position: 'relative' }}>
          {/* Contextual Manual Edit Toolbar */}
          {isManualEditMode && (
            <ManualEditToolbar
              document={activeDoc}
              selectedBlockId={selectedBlockId}
              selectedSectionId={activeDoc.sections[0]?.id || null}
              onSelectBlock={(bId) => setSelectedBlockId(bId)}
              onUpdateBlockData={handleUpdateBlockData}
              onRemoveBlock={handleRemoveBlock}
              onDuplicateBlock={handleDuplicateBlock}
              onReorderBlockLayer={(sId, bId, action) => {
                setCustomCoverDoc((prev) => {
                  const base = prev || compiledDocument;
                  return blockOps.reorderBlockLayer(base, sId, bId, action);
                });
                setSaveStatus('dirty');
              }}
              onNudgeBlock={handleNudgeBlock}
              onInsertShape={handleInsertShape}
              onInsertBlock={(sId, blockType) => {
                if (blockType === 'shape') {
                  handleInsertShape(sId, 'rectangle');
                }
              }}
            />
          )}

          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 0' }}>
            <DocumentRenderer
              document={activeDoc}
              manualEditOptions={{
                isManualEditMode,
                selectedBlockId,
                selectedSectionId: activeDoc.sections[0]?.id || null,
                onSelectBlock: (blockId) => setSelectedBlockId(blockId),
                onUpdateText: (sectionId, blockId, text) => handleUpdateBlockData(sectionId, blockId, { text }),
                onNudge: handleNudgeBlock,
                onDelete: handleRemoveBlock,
                onDuplicate: handleDuplicateBlock,
              }}
            />
          </div>
        </div>
      </div>

      {/* Template Selection Modal */}
      <TemplateSelectorModal
        isOpen={isTemplateModalOpen}
        selectedTemplateId={activeTemplate.id}
        onClose={() => setIsTemplateModalOpen(false)}
        onSelectTemplate={handleSelectTemplate}
      />

      {/* Export Preview Modal */}
      <ExportPreviewModal
        isOpen={isExportPreviewOpen}
        onClose={() => setIsExportPreviewOpen(false)}
        document={activeDoc}
        defaultFormat={exportPreviewFormat}
        onExportPdf={handleExecutePdfExport}
        onExportDocx={handleExecuteDocxExport}
      />
    </div>
  );
};
